import React from 'react';
import { Link } from 'react-router';
import { Card } from '@repo/ui/Card';
import { ButtonLink } from '@repo/ui/Button';

import { protocolLabel } from '@/features/switches/lib/protocolLabel';
import { switchTabPath } from '@/features/switches/components/SwitchTabNav';
import { useSwitchNumberCount } from '@/features/switches/hooks/useSwitchNumberCount';
import { SWITCH_ANCHOR } from './ChangeDidSwitchForm';

type NameValue = { name: string; value: string };

type ChannelOptions = {
  customHeaders?: NameValue[] | null;
  customParams?: NameValue[] | null;
} | null;

export type ForwardingSwitch = {
  id: string;
  name: string;
  smsProtocol?: string | null;
  smsEndpoints?: string[] | null;
  smsOptions?: ChannelOptions;
  mmsProtocol?: string | null;
  mmsEndpoints?: string[] | null;
  mmsOptions?: ChannelOptions;
};

interface SwitchForwardingSummaryProps {
  accountId: string | undefined;
  didId: string;
  didSwitch: ForwardingSwitch | null | undefined;
  /** May open the switch to edit its forwarding */
  canEdit: boolean;
  /** May point the number at a different switch */
  canChangeSwitch: boolean;
}

const pairs = (items: NameValue[] | null | undefined) =>
  items?.length ? (
    <ul className="flex flex-col gap-0.5">
      {items.map(({ name, value }, i) => (
        <li key={`${name}-${i}`} className="break-all">
          <span className="font-bold">{name}:</span> {value}
        </li>
      ))}
    </ul>
  ) : (
    '—'
  );

const ChannelSummary = ({
  title,
  protocol,
  endpoints,
  options,
}: {
  title: string;
  protocol: string | null | undefined;
  endpoints: string[] | null | undefined;
  options: ChannelOptions | undefined;
}) => {
  const forwarding = !!protocol && protocol !== 'NONE';
  const rows: [string, React.ReactNode][] = [
    ['Protocol', protocolLabel(protocol)],
    ['Endpoint', forwarding && endpoints?.length ? endpoints.join(', ') : '—'],
  ];
  // Headers only apply to HTTP forwarding
  if (protocol === 'HTTP') rows.push(['Headers', pairs(options?.customHeaders)]);
  if (forwarding) rows.push(['Parameters', pairs(options?.customParams)]);

  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-lg font-bold">{title}</h3>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
        {rows.map(([label, value]) => (
          <React.Fragment key={label}>
            <dt className="text-grey-600 dark:text-grey-400">{label}</dt>
            <dd className="break-all">{value}</dd>
          </React.Fragment>
        ))}
      </dl>
    </div>
  );
};

/**
 * A number's forwarding, read-only. Forwarding belongs to the switch and every
 * number on that switch shares it, so changes are made on the switch's page.
 */
const SwitchForwardingSummary = ({
  accountId,
  didId,
  didSwitch,
  canEdit,
  canChangeSwitch,
}: SwitchForwardingSummaryProps) => {
  const { count, isShared } = useSwitchNumberCount(didSwitch?.id);
  const changeSwitchPath = accountId
    ? `/a/${accountId}/numbers/${didId}/update#${SWITCH_ANCHOR}`
    : null;

  if (!didSwitch) {
    return (
      <Card
        title="No switch"
        description="This number has no switch, so its messages are not forwarded."
      >
        {canChangeSwitch && changeSwitchPath && (
          <div className="mt-6">
            <ButtonLink as={Link} href={changeSwitchPath}>
              Choose switch
            </ButtonLink>
          </div>
        )}
      </Card>
    );
  }

  const sharedNote = isShared
    ? count !== null
      ? ` It's shared with ${count - 1} other ${count - 1 === 1 ? 'number' : 'numbers'}.`
      : " It's shared with other numbers."
    : '';

  return (
    <Card
      title={`Switch: ${didSwitch.name}`}
      description={`Messages received on this number are forwarded using its switch's settings.${sharedNote}`}
    >
      <div className="grid grid-cols-1 gap-8 mt-6 md:grid-cols-2">
        <ChannelSummary
          title="SMS forwarding"
          protocol={didSwitch.smsProtocol}
          endpoints={didSwitch.smsEndpoints}
          options={didSwitch.smsOptions}
        />
        <ChannelSummary
          title="MMS forwarding"
          protocol={didSwitch.mmsProtocol}
          endpoints={didSwitch.mmsEndpoints}
          options={didSwitch.mmsOptions}
        />
      </div>

      {(canEdit || canChangeSwitch) && accountId && (
        <div className="flex flex-wrap gap-3 items-center pt-6 mt-8 border-t border-grey-400 dark:border-grey-700">
          {canEdit && (
            <ButtonLink as={Link} href={switchTabPath(accountId, didSwitch.id, 'forwarding')}>
              Edit switch forwarding
            </ButtonLink>
          )}
          {canChangeSwitch && changeSwitchPath && (
            <ButtonLink as={Link} variant="secondary" href={changeSwitchPath}>
              Change switch
            </ButtonLink>
          )}
        </div>
      )}
    </Card>
  );
};

export default SwitchForwardingSummary;
